import React, { useState, useCallback } from 'react';
import {
  message,
  theme,
  Typography,
  Modal,
  Select,
  Input,
  Form,
  Tag,
} from 'antd';
import {
  ThunderboltOutlined,
} from '@ant-design/icons';
import CharacterCheckpointsDrawer from './CharacterCheckpointsDrawer';
import PanelImageModal from './PanelImageModal';
import BulkPanelModal from './BulkPanelModal';
import {
  CreateProcessModal,
  ProcessDetailModal,
  ExtractCharactersModal,
  ScenePreviewModal,
  ReExtractPanelsModal,
  ReviewResultModal,
  PlanVisualsModal,
  ProcessPanelRows,
  SceneOutfitsDrawer,
} from './process/index';
import { 
  SourceProcess, 
  SourceChapter, 
  SourceCharacter, 
  ExtractedCharacter, 
  SourceScene, 
  ExtractedScene, 
  SourcePanel, 
  ExtractedPanel,
} from '../../types';
import { sourceStoriesApi, CreateSourceProcessRequest, SavePanelsRequest } from '../../services/api';
import { useApi, useAsyncOperation, useIsMobile } from '../../hooks';
import DraftPanelsPreviewModal from './DraftPanelsPreviewModal';
import AutoEpisodeConfigModal from '../../components/admin/tasks/AutoEpisodeConfigModal';
import AutoCloneProcessConfigModal from '../../components/admin/tasks/AutoCloneProcessConfigModal';

const { Text } = Typography;

interface ProcessTabProps {
  storyId: string;
  chapters: SourceChapter[];
}

const ProcessTab: React.FC<ProcessTabProps> = ({ storyId, chapters }) => {
  const isMobile = useIsMobile();
  const { token: { colorBorder } } = theme.useToken();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<SourceProcess | null>(null);
  const [isExtractModalVisible, setIsExtractModalVisible] = useState(false);
  const [extractedCharacters, setExtractedCharacters] = useState<ExtractedCharacter[]>([]);
  const [form] = Form.useForm();

  // Scene-related state
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [activeSceneKeys, setActiveSceneKeys] = useState<Record<string, string | string[]>>({});
  const [activeDetailKeys, setActiveDetailKeys] = useState<Record<string, string | string[]>>({});
  const [scenesData, setScenesData] = useState<Record<string, SourceScene[]>>({});
  const [scenesLoading, setScenesLoading] = useState<Record<string, boolean>>({});
  const [extractedScenes, setExtractedScenes] = useState<ExtractedScene[]>([]);
  const [isScenePreviewModalVisible, setIsScenePreviewModalVisible] = useState(false);
  const [currentExtractingProcessId, setCurrentExtractingProcessId] = useState<string | null>(null);

  const [isSceneOutfitsVisible, setIsSceneOutfitsVisible] = useState(false);
  const [selectedSceneForOutfits, setSelectedSceneForOutfits] = useState<SourceScene | null>(null);

  // Panel-related state
  const [panelsData, setPanelsData] = useState<Record<string, SourcePanel[]>>({});
  const [panelsLoading, setPanelsLoading] = useState<Record<string, boolean>>({});
  const [currentExtractingSceneId, setCurrentExtractingSceneId] = useState<string | null>(null);
  const [selectedSceneForPanels, setSelectedSceneForPanels] = useState<SourceScene | null>(null);
  
  const [isPlanVisualsModalVisible, setIsPlanVisualsModalVisible] = useState(false);
  const [planVisualsScene, setPlanVisualsScene] = useState<SourceScene | null>(null);
  const [planVisualsStrategy, setPlanVisualsStrategy] = useState<'ALL_IMAGE' | 'ALL_VIDEO' | 'AUTO_MIX'>('ALL_IMAGE');

  const [isPanelImageModalVisible, setIsPanelImageModalVisible] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<SourcePanel | null>(null);

  // Draft Panels state (Epic 2)
  const [isDraftPanelsModalVisible, setIsDraftPanelsModalVisible] = useState(false);
  const [draftedPanels, setDraftedPanels] = useState<ExtractedPanel[]>([]);

  // Bulk Panel Image Generation state
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [selectedSceneForBulk, setSelectedSceneForBulk] = useState<SourceScene | null>(null);

  // Re-extract Panels state
  const [isReExtractModalVisible, setIsReExtractModalVisible] = useState(false);
  const [reExtractScene, setReExtractScene] = useState<SourceScene | null>(null);
  const [reExtractComment, setReExtractComment] = useState('');
  const [reExtractStep, setReExtractStep] = useState<'input' | 'preview'>('input');
  const [reExtractedPanels, setReExtractedPanels] = useState<ExtractedPanel[]>([]);

  // AI Review Scene state
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [reviewScene, setReviewScene] = useState<SourceScene | null>(null);
  const [reviewResult, setReviewResult] = useState<{ critiques: string[]; issues: Array<{ tag: string; description: string }>; suggestedComment: string } | null>(null);

  const [isCharacterCheckpointsVisible, setIsCharacterCheckpointsVisible] = useState(false);
  const [selectedCharacterForCheckpoints, setSelectedCharacterForCheckpoints] = useState<SourceCharacter | null>(null);

  const [isAutoEpisodeModalVisible, setIsAutoEpisodeModalVisible] = useState(false);
  const [autoEpisodeProcess, setAutoEpisodeProcess] = useState<SourceProcess | null>(null);

  const [isAutoCloneModalVisible, setIsAutoCloneModalVisible] = useState(false);
  const [autoCloneProcess, setAutoCloneProcess] = useState<SourceProcess | null>(null);

  const { data: allPanelsData, refresh: refreshAllPanels } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]),
    { immediate: true }
  );
  const allPanels = React.useMemo(() => allPanelsData?.data || [], [allPanelsData]);

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch processes
  const {
    data: processesData,
    loading: processesLoading,
    refresh: refreshProcesses,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]),
    { immediate: true }
  );

  // Fetch characters for the story to display names instead of IDs
  const {
    data: storyCharactersData,
    refresh: refreshCharacters,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceCharacters(storyId, { pageSize: 1000 }), [storyId]),
    { immediate: true }
  );

  const storyCharacters = React.useMemo(() => storyCharactersData?.data || [], [storyCharactersData]);

  // Phase 3.1: Fetch all checkpoints for the story
  const {
    data: allCheckpointsData,
    refresh: refreshCheckpoints,
  } = useApi(
    useCallback(() => sourceStoriesApi.getAllCharacterCheckpoints(storyId), [storyId]),
    { immediate: true }
  );
  const allCheckpoints = React.useMemo(() => allCheckpointsData?.data || [], [allCheckpointsData]);

  const characterMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    storyCharacters.forEach(c => {
      map[c.id] = c.name;
    });
    return map;
  }, [storyCharacters]);

  const getCharacterName = (id: string) => characterMap[id] || `Unknown (${id.substring(0, 8)})`;

  const getProcessCompletionStats = useCallback((processId: string) => {
    const processPanels = allPanels.filter((p: SourcePanel) => p.processId === processId);
    if (processPanels.length === 0) return { isComplete: false, completedCount: 0, totalCount: 0 };

    const totalCount = processPanels.length;
    const completedCount = processPanels.filter((p: SourcePanel) => !!p.imagePath).length;
    
    return {
      isComplete: completedCount === totalCount && totalCount > 0,
      completedCount,
      totalCount
    };
  }, [allPanels]);

  const getSceneDoneCount = useCallback((sceneId: string) => {
    const scenePanels = allPanels.filter((p: SourcePanel) => p.sceneId === sceneId);
    if (scenePanels.length === 0) return 0;

    return scenePanels.filter((p: SourcePanel) => !!p.imagePath).length;
  }, [allPanels]);

  const handleCreate = () => {
    form.resetFields();

    // Determine the next chapter to start from
    let nextChapterId = undefined;
    if (chapters && chapters.length > 0) {
      if (!processesData || processesData.data.length === 0) {
        // If no processes yet, start from the first chapter
        nextChapterId = chapters[0].id;
      } else {
        // Find the maximum index used in any existing process
        let maxIndex = -1;
        processesData.data.forEach(p => {
          p.chapterIds.forEach(id => {
            const idx = chapters.findIndex(c => c.id === id);
            if (idx > maxIndex) maxIndex = idx;
          });
        });

        // The next chapter is the one right after the highest index found
        if (maxIndex !== -1 && maxIndex < chapters.length - 1) {
          nextChapterId = chapters[maxIndex + 1].id;
        } else if (maxIndex === -1) {
          // Fallback to first chapter if somehow no chapters matched
          nextChapterId = chapters[0].id;
        }
      }
    }

    if (nextChapterId) {
      form.setFieldsValue({ 
        startChapterId: nextChapterId,
        videoForm: 'LONG' 
      });
    } else {
      form.setFieldsValue({
        videoForm: 'LONG'
      });
    }

    setIsModalVisible(true);
  };

  const handleDelete = async (process: SourceProcess) => {
    await executeAsync(
      () => sourceStoriesApi.deleteSourceProcess(storyId, process.id),
      { successMessage: 'Process deleted successfully!' }
    );
    refreshProcesses();
  };

  const handleViewDetail = (process: SourceProcess) => {
    setSelectedProcess(process);
    setIsDetailModalVisible(true);
  };

  const handleExtractCharacters = async (process: SourceProcess) => {
    setSelectedProcess(process);
    const result = await executeAsync(
      () => sourceStoriesApi.extractCharacters(storyId, process.id),
      { successMessage: 'AI character extraction completed!' }
    );
    if (result && result.data) {
      setExtractedCharacters(result.data);
      setIsExtractModalVisible(true);
    }
  };

  const handleImportCharacters = async () => {
    if (!selectedProcess) return;
    
    // Auto select ALL extracted characters (both new and old) as per user request
    const charactersToImport = extractedCharacters;

    if (charactersToImport.length === 0) {
      message.warning('No characters found to import.');
      return;
    }

    await executeAsync(
      () => sourceStoriesApi.saveExtractedCharacters(storyId, selectedProcess.id, { characters: charactersToImport }),
      { successMessage: `Successfully updated characters for this process!` }
    );

    setIsExtractModalVisible(false);
    // Refresh characters first to ensure we have the new records
    await refreshCharacters();
    // Then refresh processes to get the updated characterIds
    await refreshProcesses();
  };

  // Scene handlers
  const fetchScenes = async (processId: string) => {
    setScenesLoading(prev => ({ ...prev, [processId]: true }));
    try {
      const result = await sourceStoriesApi.getProcessScenes(storyId, processId);
      setScenesData(prev => ({ ...prev, [processId]: result.data || [] }));
    } catch (error) {
      message.error('Failed to load scenes');
    } finally {
      setScenesLoading(prev => ({ ...prev, [processId]: false }));
    }
  };

  const handleExtractScenes = async (process: SourceProcess) => {
    setCurrentExtractingProcessId(process.id);
    const result = await executeAsync(
      () => sourceStoriesApi.extractScenes(storyId, process.id),
      { successMessage: 'AI scene extraction completed!' }
    );
    if (result && result.data) {
      setExtractedScenes(result.data);
      setSelectedProcess(process);
      setIsScenePreviewModalVisible(true);
    }
    setCurrentExtractingProcessId(null);
  };

  const handleSaveScenes = async () => {
    if (!selectedProcess) return;

    await executeAsync(
      () => sourceStoriesApi.saveProcessScenes(storyId, selectedProcess.id, { scenes: extractedScenes }),
      { successMessage: 'Scenes saved successfully!' }
    );

    setIsScenePreviewModalVisible(false);
    // Refresh scenes for this process
    await fetchScenes(selectedProcess.id);
  };

  // Panel handlers
  const fetchPanels = async (sceneId: string) => {
    setPanelsLoading(prev => ({ ...prev, [sceneId]: true }));
    try {
      const result = await sourceStoriesApi.getSourcePanels(storyId, sceneId);
      const panels = result.data || [];
      setPanelsData(prev => ({ ...prev, [sceneId]: panels }));
    } catch (error) {
      message.error('Failed to load panels');
    } finally {
      setPanelsLoading(prev => ({ ...prev, [sceneId]: false }));
    }
  };
  
  const handleDraftPanels = async (scene: SourceScene) => {
    setSelectedSceneForPanels(scene);
    setCurrentExtractingSceneId(scene.id);
    const result = await executeAsync(
      () => sourceStoriesApi.draftPanels(storyId, scene.id),
      { successMessage: 'AI đã lên dàn ý panel thành công!' }
    );
    if (result && result.data) {
      setDraftedPanels(result.data);
      setIsDraftPanelsModalVisible(true);
    }
    setCurrentExtractingSceneId(null);
  };

  const handleSaveDraftPanels = async () => {
    if (!selectedSceneForPanels) return;

    const payload: SavePanelsRequest = {
      processId: selectedSceneForPanels.processId,
      panels: draftedPanels,
    };

    await executeAsync(
      () => sourceStoriesApi.saveExtractedPanels(storyId, selectedSceneForPanels.id, payload),
      { successMessage: 'Đã lưu dàn ý panel thành công!' }
    );

    setIsDraftPanelsModalVisible(false);
    await fetchPanels(selectedSceneForPanels.id);
    await fetchScenes(selectedSceneForPanels.processId);
    refreshProcesses();
  };

  const handlePlanVisuals = (scene: SourceScene) => {
    setPlanVisualsScene(scene);
    setPlanVisualsStrategy('ALL_IMAGE');
    setIsPlanVisualsModalVisible(true);
  };

  const handleConfirmPlanVisuals = async () => {
    if (!planVisualsScene) return;

    await executeAsync(
      () => sourceStoriesApi.planSceneVisuals(storyId, planVisualsScene.id, planVisualsStrategy),
      { successMessage: 'AI đã hoàn thành lập kế hoạch hình ảnh cho scene!' }
    );

    setIsPlanVisualsModalVisible(false);
    await fetchPanels(planVisualsScene.id);
    await fetchScenes(planVisualsScene.processId);
  };

  const handleRowExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
      // Load scenes when expanding
      if (!record.isGroup && !scenesData[record.id]) {
        fetchScenes(record.id);
      }
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
    }
  };

  const handleSceneExpand = (sceneId: string) => {
    if (!panelsData[sceneId]) {
      fetchPanels(sceneId);
    }
  };

  const handleSceneUpdated = (processId: string, updatedScene: SourceScene) => {
    setScenesData(prev => {
      const currentScenes = prev[processId] || [];
      const updatedScenes = currentScenes.map(s => 
        s.id === updatedScene.id ? updatedScene : s
      );
      return { ...prev, [processId]: updatedScenes };
    });
  };

  const handlePanelUpdated = (sceneId: string, updatedPanel: SourcePanel) => {
    setPanelsData(prev => {
      const currentPanels = prev[sceneId] || [];
      const updatedPanels = currentPanels.map(p => 
        p.id === updatedPanel.id ? updatedPanel : p
      );
      return { ...prev, [sceneId]: updatedPanels };
    });
  };

  const handleOpenSceneOutfits = (scene: SourceScene) => {
    setSelectedSceneForOutfits(scene);
    setIsSceneOutfitsVisible(true);
  };

  const handleOutfitGenerated = (sceneId: string, updatedOutfits: any[]) => {
    // Update the local scenesData to reflect the new outfits
    setScenesData(prev => {
      const newScenesData = { ...prev };
      for (const processId in newScenesData) {
        const scenes = [...newScenesData[processId]];
        const sceneIdx = scenes.findIndex(s => s.id === sceneId);
        if (sceneIdx >= 0) {
          scenes[sceneIdx] = { ...scenes[sceneIdx], outfits: updatedOutfits };
          newScenesData[processId] = scenes;
          break;
        }
      }
      return newScenesData;
    });

    // Also update selectedSceneForOutfits if it's currently open
    if (selectedSceneForOutfits?.id === sceneId) {
      setSelectedSceneForOutfits(prev => prev ? { ...prev, outfits: updatedOutfits } : null);
    }
  };

  const handleManagePanelImage = (panel: SourcePanel) => {
    setSelectedPanel(panel);
    setIsPanelImageModalVisible(true);
  };

  const handleDeletePanel = (panel: SourcePanel) => {
    Modal.confirm({
      title: 'Xác nhận xóa Panel',
      content: `Bạn có chắc chắn muốn xóa Panel #${panel.sceneOrderIndex}? Các panel phía sau sẽ tự động được đánh lại số thứ tự.`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await sourceStoriesApi.deleteSourcePanel(storyId, panel.sceneId, panel.id);
          message.success('Đã xóa panel thành công');
          fetchPanels(panel.sceneId);
          fetchScenes(panel.processId);
          refreshProcesses();
        } catch (error) {
          message.error('Không thể xóa panel');
        }
      },
    });
  };

  const handleAiRefinePanel = (panel: SourcePanel) => {
    const values = {
      comment: '',
      strategy: 'AUTO' as 'AUTO' | 'IMAGE' | 'VIDEO'
    };

    Modal.confirm({
      title: 'Hiệu chỉnh nội dung bằng AI',
      icon: <ThunderboltOutlined />,
      width: 600,
      content: (
        <div style={{ marginTop: 16 }}>
          <Text strong>Chiến lược Media:</Text>
          <Select
            defaultValue="AUTO"
            onChange={(val: any) => { values.strategy = val; }}
            style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
            options={[
              { value: 'AUTO', label: '🤖 Auto (AI tự quyết định)' },
              { value: 'IMAGE', label: '🖼️ Image (Bắt buộc là ảnh tĩnh)' },
              { value: 'VIDEO', label: '🎬 Video (Bắt buộc là video)' },
            ]}
          />
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Yêu cầu hiệu chỉnh:
          </Text>
          <Input.TextArea
            rows={4}
            placeholder="Ví dụ: Cho nhân vật A khóc nức nở, viết lại lời thoại cho xúc động hơn..."
            onChange={(e: any) => { values.comment = e.target.value; }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
            AI sẽ viết lại Caption, Visual Description và cập nhật danh sách nhân vật dựa trên yêu cầu của bạn.
          </Text>
        </div>
      ),
      okText: 'Gửi yêu cầu',
      cancelText: 'Hủy',
      onOk: async () => {
        if (!values.comment || !values.comment.trim()) {
          message.warning('Vui lòng nhập yêu cầu hiệu chỉnh');
          return Promise.reject();
        }
        try {
          await sourceStoriesApi.aiRefinePanel(storyId, panel.sceneId, panel.id, values.comment, values.strategy);
          message.success('Đã hiệu chỉnh nội dung thành công!');
          fetchPanels(panel.sceneId);
          refreshProcesses();
        } catch (error) {
          message.error('Lỗi khi hiệu chỉnh nội dung bằng AI');
        }
      },
    });
  };

  const handleManageCharacterCheckpoints = (character: SourceCharacter) => {
    setSelectedCharacterForCheckpoints(character);
    setIsCharacterCheckpointsVisible(true);
  };

  const handleUploadPanelImage = async (panel: SourcePanel, file: File) => {
    try {
      await sourceStoriesApi.uploadPanelImage(storyId, panel.sceneId, panel.id, file);
      message.success('Upload ảnh thành công');
      fetchPanels(panel.sceneId);
      refreshProcesses();
    } catch (error: any) {
      message.error('Upload ảnh thất bại: ' + (error.message || 'Unknown error'));
    }
  };

  const handleCloneShortProcess = async (process: SourceProcess) => {
    await executeAsync(
      () => sourceStoriesApi.cloneShortProcess(storyId, process.id),
      { successMessage: 'Đã tạo bản sao Short Video thành công!' }
    );
    refreshProcesses();
  };

  const handleRefreshAll = useCallback(() => {
    refreshProcesses();
    refreshAllPanels();
    refreshCharacters();
    refreshCheckpoints();
  }, [refreshProcesses, refreshAllPanels, refreshCharacters, refreshCheckpoints]);

  const handleSubmit = async (values: any) => {
    const { startChapterId, endChapterId, name, videoForm } = values;
    
    // Find chapters in between
    const startIndex = chapters.findIndex(c => c.id === startChapterId);
    const endIndex = chapters.findIndex(c => c.id === endChapterId);
    
    if (startIndex === -1 || endIndex === -1) return;
    
    // Support both directions, but usually start < end
    const realStart = Math.min(startIndex, endIndex);
    const realEnd = Math.max(startIndex, endIndex);
    
    const chapterIds = chapters.slice(realStart, realEnd + 1).map(c => c.id);
    
    // Generate name if not provided
    let processName = name;
    if (!processName || !processName.trim()) {
      const startNum = realStart + 1;
      const endNum = realEnd + 1;
      processName = startNum === endNum ? `Chapter ${startNum} analysis` : `Chapters ${startNum}-${endNum} analysis`;
    }

    const payload: CreateSourceProcessRequest = {
      name: processName,
      chapterIds,
      videoForm,
    };

    const result = await executeAsync(
      () => sourceStoriesApi.createSourceProcess(storyId, payload),
      { successMessage: 'Process created successfully!' }
    );

    if (result) {
      setIsModalVisible(false);
      form.resetFields();
      refreshProcesses();
    }
  };

  const handleClearPanels = async (scene: SourceScene) => {
    Modal.confirm({
      title: 'Are you sure you want to clear all panels for this scene?',
      content: 'This action cannot be undone.',
      onOk: async () => {
        await executeAsync(
          () => sourceStoriesApi.clearSourcePanels(storyId, scene.id),
          { successMessage: 'Panels cleared successfully!' }
        );
        fetchPanels(scene.id);
        fetchScenes(scene.processId);
        refreshProcesses();
      }
    });
  };

  const handleReExtractPanels = (scene: SourceScene) => {
    setReExtractScene(scene);
    setReExtractComment('');
    setReExtractStep('input');
    setReExtractedPanels([]);
    setIsReExtractModalVisible(true);
  };

  const handlePreviewReExtract = async () => {
    if (!reExtractScene || !reExtractComment.trim()) {
      message.warning('Vui lòng nhập yêu cầu hiệu chỉnh');
      return;
    }

    const result = await executeAsync(
      () => sourceStoriesApi.previewReDraftPanels(storyId, reExtractScene.id, reExtractComment),
      { successMessage: 'AI đã viết lại dàn ý xong, mời bạn review!' }
    );

    if (result && result.data) {
      setReExtractedPanels(result.data);
      setReExtractStep('preview');
    }
  };

  const handleConfirmReExtract = async () => {
    if (!reExtractScene || reExtractedPanels.length === 0) return;

    await executeAsync(
      () => sourceStoriesApi.confirmReDraftPanels(storyId, reExtractScene.id, reExtractedPanels),
      { successMessage: 'Đã cập nhật lại dàn ý Panel thành công! Đừng quên thực hiện bước "Đạo diễn hình ảnh" tiếp theo nhé.' }
    );

    setIsReExtractModalVisible(false);
    fetchPanels(reExtractScene.id);
    fetchScenes(reExtractScene.processId);
    refreshProcesses();
  };

  const handleReviewScene = async (scene: SourceScene) => {
    setReviewScene(scene);
    setReviewResult(null);
    setIsReviewModalVisible(true);

    const result = await executeAsync(
      () => sourceStoriesApi.reviewSceneContent(storyId, scene.id),
      { successMessage: 'AI đã review xong!' }
    );

    if (result && result.data) {
      setReviewResult(result.data);
    }
  };

  const handleApplyReviewSuggestion = () => {
    if (!reviewResult || !reviewScene) return;
    
    const suggestion = reviewResult.suggestedComment;
    setIsReviewModalVisible(false);
    
    // Trigger re-extract flow with suggestion
    setReExtractScene(reviewScene);
    setReExtractComment(suggestion);
    setReExtractStep('input');
    setReExtractedPanels([]);
    setIsReExtractModalVisible(true);
  };

  const handleClearScenes = async (process: SourceProcess) => {
    Modal.confirm({
      title: 'Are you sure you want to clear all scenes for this process?',
      content: 'This will also clear all panels within these scenes. This action cannot be undone.',
      onOk: async () => {
        await executeAsync(
          () => sourceStoriesApi.clearSourceScenes(storyId, process.id),
          { successMessage: 'Scenes cleared successfully!' }
        );
        fetchScenes(process.id);
      }
    });
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Tag color="default">Draft</Tag>;
      case 'PROCESSING':
        return <Tag color="processing">Processing</Tag>;
      case 'COMPLETED':
        return <Tag color="success">Completed</Tag>;
      case 'FAILED':
        return <Tag color="error">Failed</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // Check if we can create a new process
  const processes = processesData?.data || [];
  const latestProcess = processes.length > 0 
    ? [...processes].sort((a, b) => b.orderIndex - a.orderIndex)[0]
    : null;
  const canCreateProcess = true

  return (
    <>
      <ProcessPanelRows
        isMobile={isMobile}
        chapters={chapters}
        processesLoading={processesLoading}
        processesData={processesData}
        expandedRowKeys={expandedRowKeys}
        handleRowExpand={handleRowExpand}
        handleCreate={handleCreate}
        canCreateProcess={canCreateProcess}
        latestProcess={latestProcess}
        scenesData={scenesData}
        scenesLoading={scenesLoading}
        storyCharacters={storyCharacters}
        asyncLoading={asyncLoading}
        selectedProcess={selectedProcess}
        currentExtractingProcessId={currentExtractingProcessId}
        currentExtractingSceneId={currentExtractingSceneId}
        activeSceneKeys={activeSceneKeys}
        setActiveSceneKeys={setActiveSceneKeys}
        handleSceneExpand={handleSceneExpand}
        panelsData={panelsData}
        panelsLoading={panelsLoading}
        activeDetailKeys={activeDetailKeys}
        setActiveDetailKeys={setActiveDetailKeys}
        getCharacterName={getCharacterName}
        getSceneDoneCount={getSceneDoneCount}
        handleExtractCharacters={handleExtractCharacters}
        handleManageCharacterCheckpoints={handleManageCharacterCheckpoints}
        handleExtractScenes={handleExtractScenes}
        handleClearScenes={handleClearScenes}
        handleDraftPanels={handleDraftPanels}
        handlePlanVisuals={handlePlanVisuals}
        setSelectedSceneForBulk={setSelectedSceneForBulk}
        setIsBulkModalVisible={setIsBulkModalVisible}
        handleReviewScene={handleReviewScene}
        handleReExtractPanels={handleReExtractPanels}
        handleClearPanels={handleClearPanels}
        handleManagePanelImage={handleManagePanelImage}
        handleUploadPanelImage={handleUploadPanelImage}
        handleAiRefinePanel={handleAiRefinePanel}
        handleDeletePanel={handleDeletePanel}
        handleCloneShortProcess={handleCloneShortProcess}
        handleViewDetail={handleViewDetail}
        handleDelete={handleDelete}
        setAutoCloneProcess={setAutoCloneProcess}
        setIsAutoCloneModalVisible={setIsAutoCloneModalVisible}
        setAutoEpisodeProcess={setAutoEpisodeProcess}
        setIsAutoEpisodeModalVisible={setIsAutoEpisodeModalVisible}
        getStatusTag={getStatusTag}
        getProcessCompletionStats={getProcessCompletionStats}
        colorBorder={colorBorder}
        handleRefresh={handleRefreshAll}
        handleOpenSceneOutfits={handleOpenSceneOutfits}
        onSceneUpdated={handleSceneUpdated}
        onPanelUpdated={handlePanelUpdated}
      />

      <CreateProcessModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        chapters={chapters}
        form={form}
        loading={asyncLoading}
      />

      <ProcessDetailModal
        visible={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        process={selectedProcess}
        storyCharacters={storyCharacters}
        chapters={chapters}
        onExtractCharacters={handleExtractCharacters}
        onManageCharacterCheckpoints={handleManageCharacterCheckpoints}
        loading={asyncLoading}
      />

      <ExtractCharactersModal
        visible={isExtractModalVisible}
        onCancel={() => setIsExtractModalVisible(false)}
        process={selectedProcess}
        extractedCharacters={extractedCharacters}
        onSave={handleImportCharacters}
        loading={asyncLoading}
      />

      <ScenePreviewModal
        visible={isScenePreviewModalVisible}
        onCancel={() => setIsScenePreviewModalVisible(false)}
        process={selectedProcess}
        extractedScenes={extractedScenes}
        onConfirm={handleSaveScenes}
        loading={asyncLoading}
        getCharacterName={getCharacterName}
      />

      <ReExtractPanelsModal
        visible={isReExtractModalVisible}
        onCancel={() => setIsReExtractModalVisible(false)}
        scene={reExtractScene}
        comment={reExtractComment}
        onCommentChange={setReExtractComment}
        step={reExtractStep}
        onStepChange={setReExtractStep}
        extractedPanels={reExtractedPanels}
        onExtract={handlePreviewReExtract}
        onConfirm={handleConfirmReExtract}
        loading={asyncLoading}
        getCharacterName={getCharacterName}
      />

      <ReviewResultModal
        visible={isReviewModalVisible}
        onCancel={() => setIsReviewModalVisible(false)}
        scene={reviewScene}
        result={reviewResult}
        onApply={handleApplyReviewSuggestion}
        loading={asyncLoading}
      />

      <PlanVisualsModal
        visible={isPlanVisualsModalVisible}
        onCancel={() => setIsPlanVisualsModalVisible(false)}
        scene={planVisualsScene}
        strategy={planVisualsStrategy}
        onStrategyChange={setPlanVisualsStrategy}
        onConfirm={handleConfirmPlanVisuals}
        loading={asyncLoading}
      />

      {selectedCharacterForCheckpoints && (
        <CharacterCheckpointsDrawer
          visible={isCharacterCheckpointsVisible}
          character={selectedCharacterForCheckpoints}
          storyId={storyId}
          onClose={() => {
            setIsCharacterCheckpointsVisible(false);
            setSelectedCharacterForCheckpoints(null);
            refreshCharacters();
            refreshCheckpoints();
          }}
        />
      )}

      {/* Epic 2: Draft Panels Modal */}
      <DraftPanelsPreviewModal
        visible={isDraftPanelsModalVisible}
        panels={draftedPanels}
        characters={storyCharacters}
        checkpoints={allCheckpoints}
        sceneTitle={selectedSceneForPanels?.title}
        loading={asyncLoading}
        onCancel={() => setIsDraftPanelsModalVisible(false)}
        onSave={handleSaveDraftPanels}
      />

      {selectedPanel && (
        <PanelImageModal
          visible={isPanelImageModalVisible}
          panel={selectedPanel}
          storyId={storyId}
          videoForm={processesData?.data.find(p => p.id === selectedPanel?.processId)?.videoForm || 'LONG'}
          getCharacterName={getCharacterName}
          onRefresh={(updatedPanel) => {
            // Update the local panels data with the refreshed panel
            const sceneId = updatedPanel.sceneId;
            setPanelsData(prev => {
              const currentScenePanels = prev[sceneId] || [];
              const updatedPanels = currentScenePanels.map(p => 
                p.id === updatedPanel.id ? updatedPanel : p
              );
              return { ...prev, [sceneId]: updatedPanels };
            });
            setSelectedPanel(updatedPanel);
          }}
          onClose={() => {
            setIsPanelImageModalVisible(false);
            const sceneId = selectedPanel.sceneId;
            const processId = selectedPanel.processId;
            setSelectedPanel(null);
            // Refresh panels for the scene to show updated image
            if (sceneId) {
              fetchPanels(sceneId);
            }
            // Also refresh scenes to update the panel count in the header
            if (processId) {
              fetchScenes(processId);
              // Refresh processes to update content completion status
              refreshProcesses();
              refreshAllPanels();
            }
          }}
        />
      )}

      {selectedSceneForBulk && (
        <BulkPanelModal
          visible={isBulkModalVisible}
          scene={selectedSceneForBulk}
          storyId={storyId}
          onRefresh={() => {
            fetchPanels(selectedSceneForBulk.id);
            fetchScenes(selectedSceneForBulk.processId);
            refreshProcesses();
            refreshAllPanels();
          }}
          onClose={() => {
            setIsBulkModalVisible(false);
            const processId = selectedSceneForBulk.processId;
            const sceneId = selectedSceneForBulk.id;
            setSelectedSceneForBulk(null);
            if (processId) {
              fetchPanels(sceneId);
              fetchScenes(processId);
              refreshProcesses();
              refreshAllPanels();
            }
          }}
        />
      )}

      {/* Auto Episode Modal */}
      {storyId && autoEpisodeProcess && (
        <AutoEpisodeConfigModal
          storyId={storyId}
          processId={autoEpisodeProcess.id}
          processName={autoEpisodeProcess.name}
          visible={isAutoEpisodeModalVisible}
          onCancel={() => {
            setIsAutoEpisodeModalVisible(false);
            setAutoEpisodeProcess(null);
          }}
          onSuccess={() => {
            setIsAutoEpisodeModalVisible(false);
            setAutoEpisodeProcess(null);
          }}
        />
      )}

      {/* Auto Clone Modal */}
      {storyId && autoCloneProcess && (
        <AutoCloneProcessConfigModal
          storyId={storyId}
          processId={autoCloneProcess.id}
          processName={autoCloneProcess.name}
          visible={isAutoCloneModalVisible}
          onCancel={() => {
            setIsAutoCloneModalVisible(false);
            setAutoCloneProcess(null);
          }}
          onSuccess={() => {
            setIsAutoCloneModalVisible(false);
            setAutoCloneProcess(null);
          }}
        />
      )}

      {selectedSceneForOutfits && (
        <SceneOutfitsDrawer
          open={isSceneOutfitsVisible}
          scene={selectedSceneForOutfits}
          storyId={storyId}
          characters={storyCharacters}
          checkpoints={allCheckpoints}
          previousScenes={(scenesData[selectedSceneForOutfits.processId] || []).filter(
            s => s.orderIndex < selectedSceneForOutfits.orderIndex
          )}
          onClose={() => {
            setIsSceneOutfitsVisible(false);
            setSelectedSceneForOutfits(null);
          }}
          onOutfitGenerated={handleOutfitGenerated}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default ProcessTab;
